import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useRoute } from '../../context/RouteContext';
import { createTourDraft, updateTour } from '../../services/tourService';
import { searchPlaces } from '../../utils/geocoding';
import { formatDistance } from '../../utils/formatters';
import { RouteThumbnail } from '../common/RouteThumbnail';
import { RichTextEditor } from './RichTextEditor';
import { INCLUSIONS } from './inclusions';
import { WEEKDAYS, nextOccurrence, formatRunsOn } from '../../utils/tourSchedule';
import { draftTourDescription } from '../../services/aiService';
import { computeCurviness } from '../../utils/curviness';
import './TourBuilder.css';

// initials of the name (max 3) + a 2-digit number → e.g. "Blue Mountains" → BM47
function suggestTourCode(name) {
  const initials = name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
  return (initials || 'MT') + String(10 + Math.floor(Math.random() * 89));
}
const linesToArr = (s) => s.split('\n').map(x => x.trim()).filter(Boolean);

const MAX_IMAGES  = 6;
const MAX_SIZE_MB = 8;

const SKILL_LEVELS = [
  { id: 'beginner',     label: 'Beginner',     desc: 'Relaxed pace, easy roads' },
  { id: 'intermediate', label: 'Intermediate',  desc: 'Some twisties, moderate distance' },
  { id: 'expert',       label: 'Expert',        desc: 'Technical roads, high pace' },
];

function MeetingInput({ value, onChange }) {
  const [q, setQ]         = useState(value?.name || '');
  const [results, setRes] = useState([]);
  const [open, setOpen]   = useState(false);
  const timerRef          = useRef(null);
  const containerRef      = useRef(null);

  useEffect(() => {
    const handler = e => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = e => {
    const val = e.target.value;
    setQ(val);
    if (!val.trim()) { setRes([]); onChange(null); return; }
    // Keep the typed text as the meeting point even if the user never picks a
    // suggestion. Selecting a suggestion below refines it with coordinates.
    onChange({ lat: null, lng: null, name: val.trim() });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try { setRes(await searchPlaces(val)); setOpen(true); } catch {}
    }, 350);
  };

  const select = place => {
    setQ(place.shortName);
    setOpen(false);
    setRes([]);
    onChange({ lat: place.lat, lng: place.lng, name: place.shortName });
  };

  return (
    <div className="tb__meeting-wrap" ref={containerRef}>
      <input
        className="tb__input"
        type="text"
        placeholder="Search for a meeting point…"
        value={q}
        onChange={handleInput}
        onFocus={() => results.length && setOpen(true)}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <ul className="tb__meeting-dropdown">
          {results.map(p => (
            <li key={p.id} className="tb__meeting-option" onMouseDown={() => select(p)}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="tb__pin">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span>{p.shortName}</span>
              <span className="tb__meeting-option-sub">{p.name.split(',').slice(1,3).join(',').trim()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function tsToDateStr(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}
function tsToTimeStr(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function TourBuilder({ onClose, onOpenProfile, editTour = null, onSaved }) {
  const isEdit = !!editTour;
  const { user, profile, subscription } = useAuth();
  const { state, showToast } = useRoute();
  const { current } = state;

  // Tour vs Lesson — locked after publish, same as registrationType.
  const [category,          setCategory]          = useState(editTour?.category ?? 'tour');
  const [learnerFriendly,   setLearnerFriendly]   = useState(editTour?.learnerFriendly ?? false);
  const [name,              setName]              = useState(editTour?.name ?? '');
  const [description,       setDescription]       = useState(editTour?.description ?? '');
  // images: { url?, file?, preview, isExisting }
  const [images,            setImages]            = useState(
    editTour?.imageUrls?.map(u => ({ url: u, preview: u, isExisting: true })) ?? []
  );
  const [startDateStr,      setStartDateStr]      = useState(tsToDateStr(editTour?.startDate));
  const [startTimeStr,      setStartTimeStr]      = useState(editTour?.startDate ? tsToTimeStr(editTour.startDate) : '09:00');
  const [endDateStr,        setEndDateStr]        = useState(tsToDateStr(editTour?.endDate));
  const [endTimeStr,        setEndTimeStr]        = useState(tsToTimeStr(editTour?.endDate));
  const [meetingPoint,      setMeetingPoint]      = useState(editTour?.meetingPoint ?? null);
  const [maxParticipants,   setMaxParticipants]   = useState(editTour?.maxParticipants ?? '');
  const [skillLevel,        setSkillLevel]        = useState(editTour?.skillLevel ?? 'intermediate');
  const [regType,           setRegType]           = useState(editTour?.registrationType ?? 'free');
  const [ticketPrice,       setTicketPrice]       = useState(editTour?.ticketPrice ?? '');
  // Lesson pricing/availability (replaces ticketPrice/max-participants/depart
  // time for lessons — private, per-booking, $/hour instead of a flat ticket).
  const [hourlyRate,        setHourlyRate]        = useState(editTour?.hourlyRate ?? '');
  const [rentBikeAvailable, setRentBikeAvailable] = useState(editTour?.rentBikeAvailable ?? false);
  const [bikeHourlyRate,    setBikeHourlyRate]    = useState(editTour?.bikeHourlyRate ?? '');
  const [availabilityStart, setAvailabilityStart] = useState(editTour?.availabilityStart ?? '09:00');
  const [availabilityEnd,   setAvailabilityEnd]   = useState(editTour?.availabilityEnd ?? '17:00');
  // Tour-product details (Phase 1)
  const [tourCode,          setTourCode]          = useState(editTour?.tourCode ?? '');
  const [departTime,        setDepartTime]        = useState(editTour?.departTime ?? '09:00');
  const [returnTime,        setReturnTime]        = useState(editTour?.returnTime ?? '');
  const [durationLabel,     setDurationLabel]     = useState(editTour?.durationLabel ?? '');
  // Stops auto-filling durationLabel once the organizer types their own —
  // never overwrite a value they deliberately chose.
  const [durationTouched,   setDurationTouched]   = useState(!!editTour?.durationLabel);
  const [minParticipants,   setMinParticipants]   = useState(editTour?.minParticipants ?? '');
  const [securityBond,      setSecurityBond]      = useState(editTour?.securityBond ?? '');
  const [inclusions,        setInclusions]        = useState(editTour?.inclusions ?? []);
  const [highlightsText,    setHighlightsText]    = useState((editTour?.highlights ?? []).join('\n'));
  const [includedExtraText, setIncludedExtraText] = useState((editTour?.includedExtra ?? []).join('\n'));
  const [notIncludedText,   setNotIncludedText]   = useState((editTour?.notIncluded ?? []).join('\n'));
  const [routeNote,         setRouteNote]         = useState(editTour?.routeNote ?? '');
  // Schedule (Phase 2): one-off date vs recurring weekdays
  const [scheduleType,      setScheduleType]      = useState(editTour?.scheduleType ?? 'oneoff');
  const [runsOn,            setRunsOn]            = useState(editTour?.runsOn ?? []);
  const [seasonStartStr,    setSeasonStartStr]    = useState(tsToDateStr(editTour?.seasonStart));
  const [seasonEndStr,      setSeasonEndStr]      = useState(tsToDateStr(editTour?.seasonEnd));
  const [loading,           setLoading]           = useState(false);
  const [drafting,          setDrafting]          = useState(false);
  const [drafted,           setDrafted]           = useState(false);
  const [error,             setError]             = useState('');
  const fileInputRef = useRef(null);
  const panelRef     = useRef(null);

  // Route attachment (create mode): pick the currently loaded route, a saved
  // route, or none. Defaults to the loaded route if there is one.
  const savedRoutes = state.saved || [];
  const currentHasRoute = current.waypoints?.some(w => w.lat != null);
  const [attachedRouteId, setAttachedRouteId] = useState(currentHasRoute ? 'current' : '');
  const attachedRoute = attachedRouteId === 'current'
    ? current
    : attachedRouteId ? savedRoutes.find(r => r.id === attachedRouteId) || null : null;
  const attachedHasRoute = attachedRoute?.waypoints?.some(w => w.lat != null);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A lesson has no one-off date — it's always "available on these weekdays",
  // so switching the Type tab to Lesson forces the recurring schedule.
  useEffect(() => {
    if (category === 'lesson' && scheduleType !== 'recurring') setScheduleType('recurring');
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFiles(files) {
    const valid = Array.from(files)
      .filter(f => f.type.startsWith('image/') && f.size <= MAX_SIZE_MB * 1024 * 1024)
      .slice(0, MAX_IMAGES - images.length);
    setImages(prev =>
      [...prev, ...valid.map(f => ({ file: f, preview: URL.createObjectURL(f), isExisting: false }))].slice(0, MAX_IMAGES)
    );
  }

  function removeImage(idx) {
    setImages(prev => {
      const img = prev[idx];
      if (!img.isExisting) URL.revokeObjectURL(img.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  const toggleInclusion = (key) =>
    setInclusions(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  // Tour-product detail fields, shared by the create + edit save paths.
  function detailFields() {
    return {
      tourCode:      (tourCode.trim() || suggestTourCode(name)),
      departTime:    departTime || null,
      returnTime:    returnTime || null,
      durationLabel: durationLabel.trim() || null,
      minParticipants: minParticipants ? parseInt(minParticipants, 10) : null,
      securityBond:  securityBond ? parseFloat(securityBond) : null,
      inclusions,
      highlights:    linesToArr(highlightsText),
      includedExtra: linesToArr(includedExtraText),
      notIncluded:   linesToArr(notIncludedText),
      routeNote:     routeNote.trim(),
    };
  }

  // Lesson-only pricing/availability, shared by the create + edit save paths.
  // Lessons are always exclusive (one booking per day) and priced per hour,
  // so maxParticipants/minParticipants/ticketPrice never apply to them.
  function lessonFields() {
    return {
      hourlyRate:        regType === 'paid' ? (parseFloat(hourlyRate) || 0) : null,
      // Bike rental is its own paid add-on — offered independently of whether
      // the lesson itself is free or paid.
      rentBikeAvailable: rentBikeAvailable,
      bikeHourlyRate:    rentBikeAvailable ? (parseFloat(bikeHourlyRate) || 0) : null,
      availabilityStart: availabilityStart || null,
      availabilityEnd:   availabilityEnd || null,
    };
  }

  function parseDate(dateStr, timeStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const [h, min]  = (timeStr || '00:00').split(':').map(Number);
    return Timestamp.fromDate(new Date(y, m - 1, d, h, min));
  }

  const toggleRunsOn = (v) =>
    setRunsOn(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  // Suggests "1 Day (8h)" / "3 Days" from the start/end date+time once both
  // are set, so the organizer isn't stuck typing it out by hand — but only
  // until they type their own value (see durationTouched above).
  useEffect(() => {
    if (durationTouched || category === 'lesson' || scheduleType !== 'oneoff' || !startDateStr) return;
    const start = parseDate(startDateStr, startTimeStr)?.toDate();
    if (!start) return;
    const end = endDateStr ? parseDate(endDateStr, endTimeStr)?.toDate() : null;
    let label;
    if (end) {
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay   = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const days = Math.round((endDay - startDay) / 86_400_000) + 1;
      label = days <= 1 ? `1 Day (${Math.max(1, Math.round((end - start) / 3_600_000))}h)` : `${days} Days`;
    } else {
      label = '1 Day';
    }
    setDurationLabel(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateStr, startTimeStr, endDateStr, endTimeStr, scheduleType, category, durationTouched]);

  // Date + schedule fields for the tour doc (one-off vs recurring).
  function scheduleFields() {
    if (scheduleType === 'recurring') {
      const seasonStart = seasonStartStr ? parseDate(seasonStartStr, '00:00') : null;
      const seasonEnd   = seasonEndStr ? parseDate(seasonEndStr, '23:59') : null;
      // Seed startDate with the next occurrence so date-based sorting/weather work.
      const no = nextOccurrence({ scheduleType: 'recurring', runsOn, seasonStart: seasonStart?.toDate?.() ?? null, seasonEnd: seasonEnd?.toDate?.() ?? null });
      let startDate = null;
      if (no) {
        const [h, mn] = (departTime || '09:00').split(':').map(Number);
        no.setHours(h, mn, 0, 0);
        startDate = Timestamp.fromDate(no);
      }
      return { scheduleType: 'recurring', runsOn, seasonStart, seasonEnd, startDate, endDate: null };
    }
    return {
      scheduleType: 'oneoff', runsOn: [], seasonStart: null, seasonEnd: null,
      startDate: parseDate(startDateStr, startTimeStr),
      endDate:   endDateStr ? parseDate(endDateStr, endTimeStr) : null,
    };
  }

  // Gemini draft from whatever's filled in so far — organizer edits the result.
  async function handleDraft() {
    if (!name.trim()) { setError('Give the tour a name first — the draft is built from your details.'); return; }
    setDrafting(true);
    setError('');
    try {
      const curv = attachedHasRoute ? computeCurviness(attachedRoute.geometry) : null;
      const html = await draftTourDescription({
        name: name.trim(),
        distanceKm: attachedHasRoute && attachedRoute.distance ? attachedRoute.distance / 1000 : null,
        curviness: curv?.label || null,
        skillLevel,
        meetingPoint: meetingPoint?.name || null,
        departTime: departTime || null,
        durationLabel: durationLabel || null,
        schedule: scheduleType === 'recurring' && runsOn.length ? formatRunsOn(runsOn) : null,
        price: regType === 'paid' ? parseFloat(ticketPrice) || null : null,
        highlights: highlightsText.split('\n').map(s => s.trim()).filter(Boolean),
        inclusions: inclusions.map(k => INCLUSIONS.find(i => i.key === k)?.label).filter(Boolean),
      });
      if (html) { setDescription(html); setDrafted(true); }
    } catch (err) {
      setError(err.message || 'Could not draft a description.');
    } finally {
      setDrafting(false);
    }
  }

  async function handlePublish() {
    if (!name.trim()) { setError('Tour name is required'); return; }
    const isLesson = category === 'lesson';
    if (scheduleType === 'recurring') {
      if (!runsOn.length) { setError(isLesson ? 'Pick at least one day you’re available.' : 'Pick at least one day the tour runs.'); return; }
      if (isLesson) {
        if (!availabilityStart || !availabilityEnd) { setError('Set your daily availability window.'); return; }
        if (availabilityEnd <= availabilityStart)   { setError('Availability end time must be after the start time.'); return; }
      } else if (!departTime) {
        setError('Set a departure time for the tour.');
        return;
      }
    } else {
      if (!startDateStr) { setError('Start date is required'); return; }
      if (endDateStr) {
        const startTs = parseDate(startDateStr, startTimeStr);
        const endTs   = parseDate(endDateStr, endTimeStr);
        if (startTs && endTs && endTs.toMillis() < startTs.toMillis()) {
          setError('End date/time cannot be before the start.');
          return;
        }
      }
    }
    if (!user) { setError('Must be signed in'); return; }
    // Bike rental is a paid add-on even on an otherwise-free lesson, so it
    // needs Stripe Connect too — not just a fully-paid lesson/tour.
    if (!isEdit && (regType === 'paid' || (isLesson && rentBikeAvailable)) && !subscription?.stripeConnectAccountId) {
      setError('Connect your Stripe account in Profile & Settings before charging for a lesson or bike hire.');
      return;
    }
    if (isLesson) {
      if (regType === 'paid' && (!hourlyRate || parseFloat(hourlyRate) < 1)) {
        setError('Enter an hourly rate of at least $1.');
        return;
      }
      if (rentBikeAvailable && (!bikeHourlyRate || parseFloat(bikeHourlyRate) < 1)) {
        setError('Enter a bike hire rate of at least $1, or turn off bike hire.');
        return;
      }
    } else if (regType === 'paid' && (!ticketPrice || parseFloat(ticketPrice) < 1)) {
      setError('Enter a ticket price of at least $1.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (isEdit) {
        // Edit mode: update existing tour in Firestore
        const originalUrls   = editTour.imageUrls || [];
        const keptUrls       = images.filter(i => i.isExisting).map(i => i.url);
        const removedUrls    = originalUrls.filter(u => !keptUrls.includes(u));
        const newImageFiles  = images.filter(i => !i.isExisting).map(i => i.file);

        await updateTour({
          tourId: editTour.id,
          tourData: {
            userId: user.uid,
            name: name.trim(),
            description,
            imageUrls: keptUrls,
            ...scheduleFields(),
            meetingPoint,
            maxParticipants: category === 'lesson' ? null : (maxParticipants ? parseInt(maxParticipants, 10) : null),
            skillLevel,
            ticketPrice: category === 'lesson' ? null : (regType === 'paid' ? parseFloat(ticketPrice) || 0 : null),
            ...detailFields(),
            ...(category === 'lesson' ? lessonFields() : {}),
          },
          newImageFiles,
          removedImageUrls: removedUrls,
        });
        onSaved?.();
        onClose();
      } else {
        // Create mode: publishing is free — save straight to published.
        // Lessons never carry a route — hidden from the UI above, but guard
        // here too in case a route happens to be loaded in the background.
        const isLesson = category === 'lesson';
        const useRoute = !isLesson && attachedHasRoute;
        const tourData = {
          category,
          learnerFriendly,
          name: name.trim(),
          description,
          waypoints:       useRoute ? attachedRoute.waypoints : [],
          geometry:        useRoute ? attachedRoute.geometry  : null,
          distance:        useRoute ? attachedRoute.distance  : null,
          duration:        useRoute ? attachedRoute.duration  : null,
          ...scheduleFields(),
          meetingPoint,
          // Lessons don't use a headcount cap — availability is enforced by
          // actual time-overlap checks at booking time, not a per-date count.
          maxParticipants: isLesson ? null : (maxParticipants ? parseInt(maxParticipants, 10) : null),
          // learnerFriendly is the real signal for a lesson; skillLevel is a
          // tour-pace concept with no selector shown for lessons.
          skillLevel:      isLesson ? 'beginner' : skillLevel,
          registrationType: regType,
          ticketPrice:      isLesson ? null : (regType === 'paid' ? parseFloat(ticketPrice) || 0 : null),
          ...detailFields(),
          ...(isLesson ? lessonFields() : {}),
        };
        await createTourDraft({
          user,
          tourData,
          imageFiles: images.filter(i => !i.isExisting).map(i => i.file),
          publish: true,
        });
        showToast?.('Tour published!', 'success');
        onSaved?.();
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  // Clicking the dimmed backdrop (easy to do by accident, e.g. reaching for
  // the map behind it) used to discard the whole form with no warning.
  function requestClose() {
    const dirty = !!(
      name.trim() || description.trim() || meetingPoint || images.length > 0
      || startDateStr || endDateStr || ticketPrice || hourlyRate || attachedHasRoute
    );
    if (dirty && !window.confirm('Discard this tour? Your changes haven’t been saved.')) return;
    onClose();
  }

  // Defense-in-depth: creating a NEW tour/lesson requires a verified business
  // profile (enforced for real in firestore.rules tours/{tourId}.allow create).
  // Editing an existing tour is exempt — grandfathered, matches the ungated
  // allow update rule. Shouldn't be reachable via the UI (CommunityPanel
  // already hides "Create Tour" for non-verified accounts) — this just guards
  // stale state/races.
  if (!isEdit && profile?.businessVerifiedStatus !== 'verified') {
    return createPortal(
      <div className="tb-overlay" onClick={e => e.target.classList.contains('tb-overlay') && onClose()}>
        <aside className="tb-panel" ref={panelRef}>
          <div className="tb__header">
            <h2 className="tb__title">Create Tour</h2>
            <button className="tb__close" onClick={onClose} aria-label="Close">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
              </svg>
            </button>
          </div>
          <p style={{ padding: 20 }}>Only verified businesses can create tours or lessons. Apply from your profile to get started.</p>
        </aside>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="tb-overlay" onClick={e => e.target.classList.contains('tb-overlay') && requestClose()}>
      <aside className="tb-panel" ref={panelRef}>
        {/* Header */}
        <div className="tb__header">
          <div>
            <h2 className="tb__title">{isEdit ? 'Edit Tour' : 'Create Tour'}</h2>
            <p className="tb__subtitle">
              {isEdit
                ? 'Update tour details — route cannot be changed'
                : category === 'lesson'
                  ? 'Riders book by meeting point — no route needed for a lesson'
                  : attachedHasRoute ? 'Route attached — riders can load it' : 'No route attached'}
            </p>
          </div>
          <button className="tb__close" onClick={requestClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="tb__body">

          {/* Type — locked after publishing, same as Registration below */}
          {!isEdit ? (
            <div className="tb__field">
              <label className="tb__label">Type</label>
              <div className="tb__reg-tabs">
                <button
                  type="button"
                  className={`tb__reg-tab${category === 'tour' ? ' tb__reg-tab--active' : ''}`}
                  onClick={() => setCategory('tour')}
                >Guided Tour</button>
                <button
                  type="button"
                  className={`tb__reg-tab${category === 'lesson' ? ' tb__reg-tab--active' : ''}`}
                  onClick={() => setCategory('lesson')}
                >Lesson / Coaching</button>
              </div>
              {category === 'lesson' && (
                <label className="tb__check-row">
                  <input type="checkbox" checked={learnerFriendly} onChange={e => setLearnerFriendly(e.target.checked)} />
                  <span>Suitable for Learner-licence riders</span>
                </label>
              )}
            </div>
          ) : category === 'lesson' ? (
            <div className="tb__field">
              <label className="tb__label">Type</label>
              <p className="tb__reg-note">Lesson / Training{learnerFriendly ? ' · Learner-friendly' : ''} — can’t be changed after publishing.</p>
            </div>
          ) : null}

          {/* Tour name */}
          <div className="tb__field">
            <label className="tb__label">Tour name <span className="tb__req">*</span></label>
            <input
              className="tb__input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Coastal Highway Blast"
              maxLength={80}
            />
          </div>

          {/* Tour code */}
          <div className="tb__field">
            <label className="tb__label">Tour code <span className="tb__optional">(optional — auto if blank)</span></label>
            <input
              className="tb__input tb__input--sm"
              type="text"
              value={tourCode}
              onChange={e => setTourCode(e.target.value.toUpperCase())}
              placeholder="e.g. MT2"
              maxLength={8}
            />
          </div>

          {/* Description */}
          <div className="tb__field">
            <div className="tb__label-row">
              <label className="tb__label">Description <span className="tb__optional">(optional)</span></label>
              <button
                type="button"
                className="tb__draft-btn"
                onClick={handleDraft}
                disabled={drafting}
                title="Draft a description from your tour details — you can edit it after"
              >
                {drafting ? 'Drafting…' : '✦ Draft with AI'}
              </button>
            </div>
            <RichTextEditor value={description} onChange={setDescription} />
            <span className="tb__draft-hint">
              {drafted && !drafting
                ? 'Review the draft — it’s built only from the details you’ve entered.'
                : 'Tip: fill in the tour details below first (route, schedule, highlights, inclusions) — the AI draft is written from them, so more details = a better description.'}
            </span>
          </div>

          {/* Route attachment (create only — route is locked after publishing).
              Lessons happen at/near the meeting point, not along a route. */}
          {!isEdit && category !== 'lesson' && (
            <div className="tb__field">
              <label className="tb__label">Route <span className="tb__optional">(optional)</span></label>
              <select
                className="tb__input tb__select"
                value={attachedRouteId}
                onChange={e => setAttachedRouteId(e.target.value)}
              >
                <option value="">No route</option>
                {currentHasRoute && (
                  <option value="current">
                    Currently loaded route{current.name && current.name !== 'My Route' ? ` — ${current.name}` : ''}
                  </option>
                )}
                {savedRoutes.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.distance != null ? ` · ${formatDistance(r.distance)}` : ''}
                  </option>
                ))}
              </select>
              {attachedHasRoute && (
                <div className="tb__route-preview">
                  <RouteThumbnail geometry={attachedRoute.geometry} waypoints={attachedRoute.waypoints} width={66} height={44} />
                  <div className="tb__route-preview-meta">
                    <span className="tb__route-preview-name">{attachedRoute.name || 'Route'}</span>
                    {attachedRoute.distance != null && (
                      <span className="tb__route-preview-dist">{formatDistance(attachedRoute.distance)}</span>
                    )}
                  </div>
                </div>
              )}
              <p className="tb__reg-note">Riders can load this route after registering for the tour. Choose “No route” to keep it private.</p>
            </div>
          )}

          {/* Schedule: one-off vs recurring — a lesson has no one-off date,
              it's always "available on these weekdays", so the toggle is
              hidden and scheduleType is forced to 'recurring' for it. */}
          {category !== 'lesson' && (
            <div className="tb__field">
              <label className="tb__label">Schedule</label>
              <div className="tb__reg-tabs">
                <button type="button" className={`tb__reg-tab${scheduleType === 'oneoff' ? ' tb__reg-tab--active' : ''}`} onClick={() => setScheduleType('oneoff')}>One-off date</button>
                <button type="button" className={`tb__reg-tab${scheduleType === 'recurring' ? ' tb__reg-tab--active' : ''}`} onClick={() => setScheduleType('recurring')}>Recurring</button>
              </div>
            </div>
          )}

          {scheduleType === 'oneoff' ? (
            <>
              <div className="tb__field">
                <label className="tb__label">Start date & time <span className="tb__req">*</span></label>
                <div className="tb__date-row">
                  <input className="tb__input tb__input--date" type="date" value={startDateStr} onChange={e => setStartDateStr(e.target.value)} />
                  <input className="tb__input tb__input--time" type="time" value={startTimeStr} onChange={e => setStartTimeStr(e.target.value)} />
                </div>
              </div>
              <div className="tb__field">
                <label className="tb__label">End date & time <span className="tb__optional">(optional)</span></label>
                <div className="tb__date-row">
                  <input className="tb__input tb__input--date" type="date" value={endDateStr} min={startDateStr || undefined} onChange={e => setEndDateStr(e.target.value)} />
                  <input className="tb__input tb__input--time" type="time" value={endTimeStr} onChange={e => setEndTimeStr(e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="tb__field">
                <label className="tb__label">{category === 'lesson' ? 'Available days' : 'Runs on'} <span className="tb__req">*</span></label>
                <div className="tb__weekdays">
                  {WEEKDAYS.map(d => (
                    <button
                      key={d.v}
                      type="button"
                      className={`tb__weekday${runsOn.includes(d.v) ? ' tb__weekday--on' : ''}`}
                      onClick={() => toggleRunsOn(d.v)}
                    >{d.short}</button>
                  ))}
                </div>
              </div>
              {category === 'lesson' && (
                <div className="tb__field">
                  <label className="tb__label">Availability window <span className="tb__req">*</span></label>
                  <div className="tb__date-row">
                    <input className="tb__input tb__input--time" type="time" value={availabilityStart} onChange={e => setAvailabilityStart(e.target.value)} title="From" />
                    <input className="tb__input tb__input--time" type="time" value={availabilityEnd} min={availabilityStart || undefined} onChange={e => setAvailabilityEnd(e.target.value)} title="Until" />
                  </div>
                  <p className="tb__reg-note">Riders can book any start time and duration inside this window on your available days.</p>
                </div>
              )}
              <div className="tb__field">
                <label className="tb__label">Season <span className="tb__optional">(optional — leave blank to run indefinitely)</span></label>
                <div className="tb__date-row">
                  <input className="tb__input tb__input--date" type="date" value={seasonStartStr} onChange={e => setSeasonStartStr(e.target.value)} title="From" />
                  <input className="tb__input tb__input--date" type="date" value={seasonEndStr} min={seasonStartStr || undefined} onChange={e => setSeasonEndStr(e.target.value)} title="Until" />
                </div>
              </div>
            </>
          )}

          {/* Departs / returns / duration — a lesson's timing is covered by
              the Availability window above instead. */}
          {category !== 'lesson' && (
            <div className="tb__field">
              <label className="tb__label">Departs / returns &amp; duration <span className="tb__optional">(optional)</span></label>
              <div className="tb__date-row">
                <input className="tb__input tb__input--time" type="time" value={departTime} onChange={e => setDepartTime(e.target.value)} title="Departs" />
                <input className="tb__input tb__input--time" type="time" value={returnTime} onChange={e => setReturnTime(e.target.value)} title="Returns" />
                <input className="tb__input" type="text" value={durationLabel} onChange={e => { setDurationLabel(e.target.value); setDurationTouched(true); }} placeholder="e.g. 1 Day (8h)" maxLength={40} />
              </div>
            </div>
          )}

          {/* Meeting point */}
          <div className="tb__field">
            <label className="tb__label">Meeting point <span className="tb__optional">(optional)</span></label>
            <MeetingInput value={meetingPoint} onChange={setMeetingPoint} />
            {meetingPoint && (
              <span className="tb__meeting-selected">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                {meetingPoint.name}
              </span>
            )}
          </div>

          {/* Max/min participants — a lesson is always one private booking per
              day, so neither applies (capacity is implicitly 1). */}
          {category !== 'lesson' && (
            <>
              <div className="tb__field">
                <label className="tb__label">Max participants <span className="tb__optional">(leave empty for unlimited)</span></label>
                <input
                  className="tb__input tb__input--sm"
                  type="number"
                  min="1"
                  max="9999"
                  value={maxParticipants}
                  onChange={e => setMaxParticipants(e.target.value)}
                  placeholder="e.g. 20"
                />
              </div>

              <div className="tb__field">
                <label className="tb__label">Min riders for the tour to run <span className="tb__optional">(optional)</span></label>
                <input
                  className="tb__input tb__input--sm"
                  type="number"
                  min="1"
                  max="9999"
                  value={minParticipants}
                  onChange={e => setMinParticipants(e.target.value)}
                  placeholder="e.g. 1"
                />
              </div>
            </>
          )}

          {/* Skill level, inclusions/highlights/bond — all tour-package concepts
              that don't map onto a single lesson (learnerFriendly already
              covers who a lesson is for). */}
          {category !== 'lesson' && (
            <>
              <div className="tb__field">
                <label className="tb__label">Skill level</label>
                <div className="tb__skill-grid">
                  {SKILL_LEVELS.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      className={`tb__skill-btn${skillLevel === s.id ? ' tb__skill-btn--active' : ''}`}
                      onClick={() => setSkillLevel(s.id)}
                    >
                      <span className="tb__skill-label">{s.label}</span>
                      <span className="tb__skill-desc">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="tb__field">
                <label className="tb__label">Inclusions <span className="tb__optional">(what the price covers)</span></label>
                <div className="tb__incl-grid">
                  {INCLUSIONS.map(inc => (
                    <button
                      key={inc.key}
                      type="button"
                      className={`tb__incl${inclusions.includes(inc.key) ? ' tb__incl--on' : ''}`}
                      onClick={() => toggleInclusion(inc.key)}
                    >
                      <span className="tb__incl-icon">{inc.icon}</span>
                      <span className="tb__incl-label">{inc.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="tb__field">
                <label className="tb__label">Tour highlights <span className="tb__optional">(one per line)</span></label>
                <textarea
                  className="tb__input tb__textarea"
                  rows={4}
                  value={highlightsText}
                  onChange={e => setHighlightsText(e.target.value)}
                  placeholder={'Echo Point\nThree Sisters\nGovetts Leap Lookout'}
                />
              </div>

              <div className="tb__field">
                <label className="tb__label">Also included <span className="tb__optional">(one per line, beyond the icons)</span></label>
                <textarea
                  className="tb__input tb__textarea"
                  rows={3}
                  value={includedExtraText}
                  onChange={e => setIncludedExtraText(e.target.value)}
                  placeholder={'Complimentary pick-up & drop-off\nThird-party insurance'}
                />
              </div>

              <div className="tb__field">
                <label className="tb__label">Not included <span className="tb__optional">(one per line)</span></label>
                <textarea
                  className="tb__input tb__textarea"
                  rows={3}
                  value={notIncludedText}
                  onChange={e => setNotIncludedText(e.target.value)}
                  placeholder={'Accommodation\nDinner\nPersonal spending'}
                />
              </div>

              <div className="tb__field">
                <label className="tb__label">Security bond (AUD) <span className="tb__optional">(optional)</span></label>
                <div className="tb__price-row">
                  <span className="tb__price-currency">$</span>
                  <input
                    className="tb__input tb__input--price"
                    type="number"
                    min="0"
                    max="99999"
                    step="50"
                    value={securityBond}
                    onChange={e => setSecurityBond(e.target.value)}
                    placeholder="e.g. 1000"
                  />
                </div>
              </div>
            </>
          )}

          {/* Route note (tours) / Notes for riders (lessons) — same underlying
              field, relabelled: a lesson has no route to describe, but riders
              still benefit from "bring your own bike", "arrive 10 min early", etc. */}
          <div className="tb__field">
            <label className="tb__label">
              {category === 'lesson' ? 'Notes for riders' : 'Route note'}
              <span className="tb__optional"> (optional)</span>
            </label>
            <textarea
              className="tb__input tb__textarea"
              rows={2}
              value={routeNote}
              onChange={e => setRouteNote(e.target.value)}
              placeholder={category === 'lesson'
                ? 'e.g. Bring your own motorcycle and full protective gear. Please arrive 15 minutes early.'
                : 'e.g. 280 km on well-maintained asphalt — suitable for all skill levels.'}
            />
          </div>

          {/* Registration — Free/Paid type is locked after publishing, but the
              ticket price stays editable for paid tours. */}
          {!isEdit ? (
            <div className="tb__field">
              <label className="tb__label">Registration</label>
              <div className="tb__reg-tabs">
                <button
                  type="button"
                  className={`tb__reg-tab${regType === 'free' ? ' tb__reg-tab--active' : ''}`}
                  onClick={() => setRegType('free')}
                >Free</button>
                <button
                  type="button"
                  className={`tb__reg-tab${regType === 'paid' ? ' tb__reg-tab--active' : ''}`}
                  onClick={() => setRegType('paid')}
                >Paid</button>
              </div>
              {category === 'lesson' ? (
                <>
                  {regType === 'paid' && (
                    <div className="tb__price-row">
                      <span className="tb__price-currency">$</span>
                      <input
                        className="tb__input tb__input--price"
                        type="number"
                        min="1"
                        max="9999"
                        step="0.01"
                        value={hourlyRate}
                        onChange={e => setHourlyRate(e.target.value)}
                        placeholder="0.00"
                      />
                      <span className="tb__price-hint">per hour · you keep 100%</span>
                    </div>
                  )}
                  {/* Bike rental is its own paid add-on, offered independently of
                      whether the lesson itself is free or paid. */}
                  <label className="tb__check-row">
                    <input type="checkbox" checked={rentBikeAvailable} onChange={e => setRentBikeAvailable(e.target.checked)} />
                    <span>Offer bike hire{regType === 'free' ? ' (paid add-on)' : ''}</span>
                  </label>
                  {rentBikeAvailable && (
                    <div className="tb__price-row">
                      <span className="tb__price-currency">$</span>
                      <input
                        className="tb__input tb__input--price"
                        type="number"
                        min="1"
                        max="9999"
                        step="0.01"
                        value={bikeHourlyRate}
                        onChange={e => setBikeHourlyRate(e.target.value)}
                        placeholder="0.00"
                      />
                      <span className="tb__price-hint">per hour, for a hired bike</span>
                    </div>
                  )}
                  {(regType === 'paid' || rentBikeAvailable) && !subscription?.stripeConnectAccountId && (
                    <div className="tb__connect-warn">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      Connect your Stripe account to receive payouts.{' '}
                      <button type="button" className="tb__connect-link" onClick={() => onOpenProfile?.()}>
                        Set up in Profile
                      </button>
                    </div>
                  )}
                </>
              ) : regType === 'paid' && (
                <>
                  <div className="tb__price-row">
                    <span className="tb__price-currency">$</span>
                    <input
                      className="tb__input tb__input--price"
                      type="number"
                      min="1"
                      max="9999"
                      step="0.01"
                      value={ticketPrice}
                      onChange={e => setTicketPrice(e.target.value)}
                      placeholder="0.00"
                    />
                    <span className="tb__price-hint">per person · you keep 100%</span>
                  </div>
                  {!subscription?.stripeConnectAccountId && (
                    <div className="tb__connect-warn">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      Connect your Stripe account to receive payouts.{' '}
                      <button type="button" className="tb__connect-link" onClick={() => onOpenProfile?.()}>
                        Set up in Profile
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : category === 'lesson' ? (
            <div className="tb__field">
              <label className="tb__label">Hourly rate</label>
              {regType === 'paid' && (
                <div className="tb__price-row">
                  <span className="tb__price-currency">$</span>
                  <input
                    className="tb__input tb__input--price"
                    type="number"
                    min="1"
                    max="9999"
                    step="0.01"
                    value={hourlyRate}
                    onChange={e => setHourlyRate(e.target.value)}
                    placeholder="0.00"
                  />
                  <span className="tb__price-hint">per hour · you keep 100%</span>
                </div>
              )}
              <label className="tb__check-row">
                <input type="checkbox" checked={rentBikeAvailable} onChange={e => setRentBikeAvailable(e.target.checked)} />
                <span>Offer bike hire{regType === 'free' ? ' (paid add-on)' : ''}</span>
              </label>
              {rentBikeAvailable && (
                <div className="tb__price-row">
                  <span className="tb__price-currency">$</span>
                  <input
                    className="tb__input tb__input--price"
                    type="number"
                    min="1"
                    max="9999"
                    step="0.01"
                    value={bikeHourlyRate}
                    onChange={e => setBikeHourlyRate(e.target.value)}
                    placeholder="0.00"
                  />
                  <span className="tb__price-hint">per hour, for a hired bike</span>
                </div>
              )}
              <p className="tb__reg-note">A new rate applies to future bookings only — riders who already paid keep their price.</p>
            </div>
          ) : regType === 'paid' ? (
            <div className="tb__field">
              <label className="tb__label">Ticket price</label>
              <div className="tb__price-row">
                <span className="tb__price-currency">$</span>
                <input
                  className="tb__input tb__input--price"
                  type="number"
                  min="1"
                  max="9999"
                  step="0.01"
                  value={ticketPrice}
                  onChange={e => setTicketPrice(e.target.value)}
                  placeholder="0.00"
                />
                <span className="tb__price-hint">per person · you keep 100%</span>
              </div>
              <p className="tb__reg-note">A new price applies to future tickets only — riders who already paid keep their price.</p>
            </div>
          ) : null}

          {/* Images */}
          <div className="tb__field">
            <label className="tb__label">Photos <span className="tb__optional">(up to {MAX_IMAGES})</span></label>
            {images.length > 0 && (
              <div className="tb__image-grid">
                {images.map((img, i) => (
                  <div key={i} className="tb__image-cell">
                    <img src={img.preview} alt="" className="tb__image-thumb" />
                    <button className="tb__image-remove" onClick={() => removeImage(i)} type="button" aria-label="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
            {images.length < MAX_IMAGES && (
              <div
                className="tb__drop-zone"
                onClick={() => fileInputRef.current?.click()}
                onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                onDragOver={e => e.preventDefault()}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>Drop photos here or click to browse</span>
              </div>
            )}
            <input ref={fileInputRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
          </div>

          {error && <div className="tb__error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="tb__footer">
          {!isEdit && (
            <div className="tb__fee-info">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
              Free to publish{regType === 'paid' ? ' · 5% booking fee added at checkout' : ''}
            </div>
          )}
          <button
            className="tb__publish-btn"
            onClick={handlePublish}
            disabled={loading || !name.trim() || (scheduleType === 'oneoff' ? !startDateStr : !runsOn.length)}
          >
            {loading ? (
              <><span className="tb__spinner" /> Saving…</>
            ) : isEdit ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                Save Changes
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                Publish Tour
              </>
            )}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}
